import React from 'react';
import styled from 'styled-components';
import Functions from '@client/style/Functions';
import { Breakpoints } from '@client/style/Variables';
import ListWithImage from '@client/modules/ListModules/ListWithImage';
import ListWithName from '@client/modules/ListModules/ListWithName';
import SingleImageEntity from '@client/modules/ListModules/SingleImageEntity';
import FilterStore, { ViewType } from '@client/modules/ListModules/FilterStore';
import { ListView } from '../ListView';
import DetailedListEntry from '../DetailedListEntry';
import { ImageData } from '../../Image';
import { CommonBoldText } from '@client/style/Text';
import { DateUtils } from '@client/core/utils/DateUtils';
import DetailedListEntryWithTimestamp from '../DetailedListEntryWithTimestamp';

const DetailedEntryList = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 40px;
	margin-top: 60px;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: grid;
		grid-template-columns: 1fr 1fr;

		gap: var(--gridGutter);
		row-gap: 80px;
	}
`;

const NoMatchText = styled(CommonBoldText)<{ color?: string }>`
	color: ${props => props.color || 'black'};
`;

export default function FilteredListContent({
	contents,
	detailedThumbnail,
	detailedThumbnailWithTimestamp,
	noMatchesText,
	backgroundColorLink,
}: {
	detailedThumbnail: boolean;
	detailedThumbnailWithTimestamp: boolean;
	noMatchesText: string;
	backgroundColorLink: any;
	contents: any[];
}) {
	// Filtering store use
	const possibleDates: Date[] | undefined = FilterStore.use(state => state.dateFilters);

	const view: ViewType = FilterStore.use(state => state.view);

	if (contents.length === 0) {
		return (
			<NoMatchText color={backgroundColorLink && Functions.hexFromRGB(backgroundColorLink?.contrastColor)}>
				{noMatchesText}
			</NoMatchText>
		);
	}

	if (view === 'list') {
		return possibleDates.map((date, i) => {
			const dayContent = contents.filter((content: any) => {
				if (content.startTime && DateUtils.isOnSameSmukDay(new Date(content?.startTime), date)) return true;
				else return false;
			});

			return (
				dayContent.length > 0 && (
					<ListWithName title={DateUtils.getWeekDayName(date)} key={'ListWithName_' + i}>
						<ListView list={dayContent}></ListView>
					</ListWithName>
				)
			);
		});
	}

	if (detailedThumbnail) {
		return (
			<DetailedEntryList>
				{contents.map(
					(
						artist: { previewImage: ImageData; previewText: string; title: string; slug: string; _prefix: string },
						i
					) => (
						<DetailedListEntry
							image={artist.previewImage}
							backgroundColorLink={backgroundColorLink}
							link={[{ internalLink: { slug: artist.slug, _prefix: artist._prefix } }]}
							subtitle={artist.previewText}
							title={artist.title}
							key={'DetailedListEntry_' + i}
						></DetailedListEntry>
					)
				)}
			</DetailedEntryList>
		);
	}

	if (detailedThumbnailWithTimestamp) {
		return (
			<DetailedEntryList>
				{contents.map(
					(
						artist: {
							previewImage: ImageData;
							previewText: string;
							title: string;
							slug: string;
							_prefix: string;
							startTime: string;
							location: any;
						},
						i
					) => (
						<DetailedListEntryWithTimestamp
							image={artist.previewImage}
							backgroundColorLink={backgroundColorLink}
							link={[{ internalLink: { slug: artist.slug, _prefix: artist._prefix } }]}
							subtitle={artist.previewText}
							title={artist.title}
							key={'DetailedListEntry_' + i}
							location={artist.location.name}
							startTime={artist.startTime}
						></DetailedListEntryWithTimestamp>
					)
				)}
			</DetailedEntryList>
		);
	}

	return (
		<ListWithImage>
			{contents.map((artist, i) => (
				<SingleImageEntity item={artist} index={i} key={'SingleImageEntity_' + i}></SingleImageEntity>
			))}
		</ListWithImage>
	);
}
